import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function SanctionProposalForm() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [formData, setFormData] = useState({
    requisition_id: "",
    basis_of_selection: "LOWEST_QUOTED",
    negotiation_requested: false,
    payment_terms: "",
    currency: "INR",
    gst_amount: 0,
    customs_amount: 0,
    other_charges: 0,
    final_price: "",
    remarks: ""
  });

  useEffect(() => { fetchRequisitions(); }, []);

  const fetchRequisitions = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/requisitions`, getAuthHeader());
      setRequisitions(r.data.filter(x => x.status === "APPROVED"));
    } catch (e) {
      toast.error("Failed to fetch requisitions");
    } finally {
      setLoading(false);
    }
  };

  const handleReqSelect = (id) => {
    const req = requisitions.find(r => r.id === id);
    setSelectedReq(req);
    setFormData(p => ({ ...p, requisition_id: id, final_price: req?.estimated_value?.toString() || "" }));
  };

  const calcTotal = () => {
    return (parseFloat(formData.final_price) || 0) + (parseFloat(formData.gst_amount) || 0) + (parseFloat(formData.customs_amount) || 0) + (parseFloat(formData.other_charges) || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.requisition_id || !formData.payment_terms || !formData.final_price) {
      toast.error("Fill required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/sanction-proposals`, {
        ...formData,
        final_price: calcTotal(),
        gst_amount: parseFloat(formData.gst_amount) || 0,
        customs_amount: parseFloat(formData.customs_amount) || 0,
        other_charges: parseFloat(formData.other_charges) || 0
      }, getAuthHeader());
      toast.success("Sanction proposal created");
      navigate("/sanction-proposals");
    } catch (e) {
      toast.error("Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="sanction-proposal-form-page">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate("/sanction-proposals")} data-testid="back-btn">← Back</Button>
        <h1 className="text-2xl font-bold text-slate-900">New Sanction Proposal</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader className="border-b"><CardTitle>Select Requisition</CardTitle></CardHeader>
          <CardContent className="p-6">
            <select value={formData.requisition_id} onChange={e => handleReqSelect(e.target.value)} className="w-full p-2 border rounded" data-testid="requisition-select">
              <option value="">Select approved requisition...</option>
              {requisitions.map(r => <option key={r.id} value={r.id}>{r.requisition_number} - ₹{r.estimated_value?.toLocaleString('en-IN')}</option>)}
            </select>
            {selectedReq && (
              <div className="mt-4 p-4 bg-slate-50 rounded grid grid-cols-4 gap-4 text-sm">
                <div><span className="text-slate-500">Dept</span><p className="font-medium">{selectedReq.department}</p></div>
                <div><span className="text-slate-500">Purpose</span><p className="font-medium">{selectedReq.purpose}</p></div>
                <div><span className="text-slate-500">Est. Value</span><p className="font-medium">₹{selectedReq.estimated_value?.toLocaleString('en-IN')}</p></div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader className="border-b"><CardTitle>Selection & Payment</CardTitle></CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Basis of Selection</Label>
              <select value={formData.basis_of_selection} onChange={e => setFormData(p => ({...p, basis_of_selection: e.target.value}))} className="w-full mt-1 p-2 border rounded" data-testid="basis-select">
                <option value="LOWEST_QUOTED">L1 - Lowest Quoted</option>
                <option value="QCBS">QCBS</option>
                <option value="SINGLE_SOURCE">Single Source</option>
                <option value="GEM_DIRECT">GeM Direct</option>
              </select>
            </div>
            <div>
              <Label>Payment Terms *</Label>
              <Textarea value={formData.payment_terms} onChange={e => setFormData(p => ({...p, payment_terms: e.target.value}))} className="mt-1" data-testid="payment-terms-input" placeholder="e.g., 100% on delivery" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Base Price (₹) *</Label>
                <Input type="number" value={formData.final_price} onChange={e => setFormData(p => ({...p, final_price: e.target.value}))} className="mt-1" data-testid="final-price-input" />
              </div>
              <div>
                <Label>GST (₹)</Label>
                <Input type="number" value={formData.gst_amount} onChange={e => setFormData(p => ({...p, gst_amount: e.target.value}))} className="mt-1" data-testid="gst-input" />
              </div>
              <div>
                <Label>Customs (₹)</Label>
                <Input type="number" value={formData.customs_amount} onChange={e => setFormData(p => ({...p, customs_amount: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label>Other (₹)</Label>
                <Input type="number" value={formData.other_charges} onChange={e => setFormData(p => ({...p, other_charges: e.target.value}))} className="mt-1" />
              </div>
            </div>
            <div className="p-4 bg-slate-900 text-white rounded flex justify-between">
              <span>Total Final Price</span>
              <span className="text-2xl font-bold">₹{calcTotal().toLocaleString('en-IN')}</span>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate("/sanction-proposals")} data-testid="cancel-btn">Cancel</Button>
          <Button type="submit" className="bg-slate-900" disabled={submitting} data-testid="submit-btn">{submitting ? "..." : "Submit Proposal"}</Button>
        </div>
      </form>
    </div>
  );
}

export default SanctionProposalForm;
